import * as Api from '@ViewModels';
import { StyleSheet, css } from 'aphrodite';
import { type FC, useState } from 'react';
import { toast } from 'sonner';

import { useUserSession } from '../../../models/hooks/appStateHooks';
import { type PhoneCallView, usePhoneCallsByCompany } from '../data';
import { IconClock, IconFileText, IconPhone } from './icons';

export type PastTranscriptsPanelProps = {
	companyId: string | undefined;
	/**
	 * Called once the rep clicks "Send to AI" on a row, after the transcript
	 * speaker-lines have been fetched and joined into a single string. The
	 * parent injects it into the active AI conversation as a user message
	 * and switches to the AI tab.
	 */
	onSendToAI: (label: string, transcriptText: string) => void;
};

/**
 * Drawer tab listing the lead's past phone calls that have a transcription.
 * Each row exposes a "Send to AI" affordance that fetches the transcript
 * speaker lines on click and hands them back via `onSendToAI` so the parent
 * can route the text into the live coaching conversation.
 */
export const PastTranscriptsPanel: FC<PastTranscriptsPanelProps> = ({ companyId, onSendToAI }) => {
	const { data: calls, isLoading, error } = usePhoneCallsByCompany(companyId);
	const userSession = useUserSession();
	const [sendingId, setSendingId] = useState<string | null>(null);

	const handleSend = async (view: PhoneCallView) => {
		const call = view.phoneCall;
		const transcriptionId = call?.transcriptionId;
		if (!transcriptionId || !call?.id) return;
		setSendingId(call.id);
		try {
			const result = await userSession.webServiceHelper.callAsync<Api.ITranscriptionSpeakerLines>(
				Api.ImpersonationBroker.composeApiUrl({
					queryParams: { type: 'SpeakerLine' },
					urlPath: `transcription/${transcriptionId}/view`,
				}),
				'GET'
			);
			const lines = result?.speakerLines ?? [];
			if (lines.length === 0) {
				toast.error('Transcript is empty.');
				return;
			}
			const text = lines.map(l => `${l.speaker}: ${l.line}`).join('\n');
			const callTimestamp = call.startTime ?? call.creationDate;
			const dateLabel = callTimestamp ? new Date(callTimestamp).toLocaleString() : 'a previous call';
			onSendToAI(dateLabel, text);
		} catch {
			toast.error('Failed to load transcript');
		} finally {
			setSendingId(null);
		}
	};

	return (
		<aside className={css(styles.panel)}>
			<header className={css(styles.header)}>
				<div className={css(styles.title)}>Past Transcripts</div>
				<div className={css(styles.subtitle)}>Previous transcribed calls for this lead</div>
			</header>
			<div className={css(styles.list)}>
				{isLoading && <div className={css(styles.empty)}>Loading…</div>}
				{error && <div className={css(styles.empty)}>Couldn’t load past calls.</div>}
				{!isLoading && !error && (!calls || calls.length === 0) && (
					<div className={css(styles.empty)}>No past transcribed calls for this lead.</div>
				)}
				{!isLoading &&
					!error &&
					(calls ?? []).map(view => (
						<TranscriptRow
							key={view.phoneCall?.id ?? `pc-${view.phoneCall?.creationDate}`}
							view={view}
							sending={sendingId === view.phoneCall?.id}
							onSend={() => void handleSend(view)}
						/>
					))}
			</div>
		</aside>
	);
};

const TranscriptRow: FC<{ view: PhoneCallView; sending: boolean; onSend: () => void }> = ({
	view,
	sending,
	onSend,
}) => {
	const call = view.phoneCall ?? {};
	const timestamp = call.startTime ?? call.creationDate;
	const when = timestamp ? new Date(timestamp) : undefined;
	const dateLabel = when
		? when.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
		: 'Unknown date';
	const timeLabel = when ? when.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : '';
	// `duration` is returned in milliseconds (~12,639 for a ~12.6s call).
	const durationLabel = formatDurationSeconds(call.duration != null ? Math.round(call.duration / 1000) : undefined);

	const counterparty = contactDisplayName(view.contacts) ?? phoneDisplay(call.phoneNumber);
	const flowLabel =
		call.direction === 'Outbound'
			? `You → ${counterparty ?? 'Unknown'}`
			: call.direction === 'Inbound'
				? `${counterparty ?? 'Unknown'} → You`
				: (counterparty ?? '');

	return (
		<div className={css(styles.row)}>
			<div className={css(styles.rowIcon)}>
				<IconFileText size={14} />
			</div>
			<div className={css(styles.rowMain)}>
				<div className={css(styles.rowTitle)}>
					{dateLabel}
					{timeLabel && <span className={css(styles.rowTime)}> · {timeLabel}</span>}
				</div>
				<div className={css(styles.rowMeta)}>
					{flowLabel && (
						<span className={css(styles.metaChip)}>
							<IconPhone size={10} />
							{flowLabel}
						</span>
					)}
					{durationLabel && (
						<span className={css(styles.metaChip)}>
							<IconClock size={10} />
							{durationLabel}
						</span>
					)}
				</div>
			</div>
			<button
				type='button'
				className={css(styles.sendBtn)}
				onClick={onSend}
				disabled={sending || !call.transcriptionId}
				title={!call.transcriptionId ? 'No transcript available' : 'Send transcript to AI conversation'}
			>
				{sending ? 'Sending…' : 'Send to AI'}
			</button>
		</div>
	);
};

function formatDurationSeconds(seconds: number | undefined): string {
	if (!seconds || seconds <= 0) return '';
	if (seconds < 60) return `${seconds}s`;
	const m = Math.floor(seconds / 60);
	const s = seconds % 60;
	return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

function contactDisplayName(contacts: PhoneCallView['contacts']): string | undefined {
	const c = contacts?.[0];
	if (!c) return undefined;
	const name = [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
	return name || undefined;
}

function phoneDisplay(p: { standard?: string; e164?: string } | undefined): string | undefined {
	return p?.standard || p?.e164 || undefined;
}

const styles = StyleSheet.create({
	panel: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: 0,
		minHeight: 0,
		height: '100%',
		background: 'var(--bg)',
	},
	header: {
		padding: '12px 16px',
		borderBottom: '1px solid var(--border)',
		flexShrink: 0,
	},
	title: {
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
		color: 'var(--ink-faint)',
	},
	subtitle: {
		fontSize: 12,
		color: 'var(--ink-muted)',
		marginTop: 2,
	},
	list: {
		flex: 1,
		overflowY: 'auto',
		minHeight: 0,
		padding: '8px 12px 16px',
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
	},
	empty: {
		padding: '24px 14px',
		fontSize: 12.5,
		color: 'var(--ink-muted)',
		textAlign: 'center',
	},
	row: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: 10,
		padding: '10px 12px',
		borderRadius: 8,
		background: 'var(--bg-elev)',
		border: '1px solid var(--border)',
	},
	rowIcon: {
		width: 26,
		height: 26,
		borderRadius: 6,
		background: 'var(--bg-sunken)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		color: 'var(--ink-muted)',
		flexShrink: 0,
	},
	rowMain: {
		flex: 1,
		minWidth: 0,
	},
	rowTitle: {
		fontSize: 13,
		fontWeight: 500,
		color: 'var(--ink)',
	},
	rowTime: {
		color: 'var(--ink-muted)',
		fontWeight: 400,
	},
	rowMeta: {
		display: 'flex',
		gap: 6,
		marginTop: 4,
		flexWrap: 'wrap',
	},
	metaChip: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 4,
		fontSize: 11,
		color: 'var(--ink-muted)',
		background: 'var(--bg-sunken)',
		padding: '2px 6px',
		borderRadius: 4,
	},
	sendBtn: {
		flexShrink: 0,
		alignSelf: 'center',
		height: 26,
		padding: '0 10px',
		borderRadius: 999,
		background: 'var(--ink)',
		color: 'var(--bg)',
		border: 'none',
		fontSize: 11,
		fontWeight: 600,
		cursor: 'pointer',
		fontFamily: 'inherit',
		':disabled': {
			opacity: 0.5,
			cursor: 'not-allowed',
		},
	},
});
